import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';
import { useProfile } from '@/modules/profiles/presentation/context/ProfileContext';
import { EnrollmentScreenLayout } from '@/presentation/layouts/EnrollmentScreenLayout';
import { CameraFeed } from '@/presentation/components/CameraFeed';
import { OnnxFaceMatcher } from '@/modules/attendance/infrastructure/face/OnnxFaceMatcher';
import { EmbeddingTimeoutError } from '@/modules/attendance/infrastructure/face/errors';
import { withTimeout } from '@/shared/utils/withTimeout';
import {
  LivenessAggregator,
  DEFAULT_AGGREGATOR_CONFIG,
  type AggregatorConfig,
} from '@/modules/attendance/domain/liveness/LivenessAggregator';
import type { QualityRuntimeConfig } from '@/modules/attendance/infrastructure/face/types';
import { logTiming } from '@/shared/utils/debugLog';
import { nextInterval } from '@/modules/attendance/infrastructure/face/pacing';
import {
  classifyTier,
  detectionScaleForTier,
} from '@/modules/attendance/infrastructure/face/DevicePerfProfile';
import { mapErrorToCode } from './mapErrorToCode';
import { runEnrollSaveGate } from '@/modules/admin/application/enrollSaveGate';
import { checkEnrollDuplicateFace } from '@/modules/admin/application/checkEnrollDuplicateFace';
import { useEnrollFaceMutation } from '@/modules/admin/application/enrollMutations';
import { FaceEmbedding } from '@/modules/attendance/domain/valueObjects/FaceEmbedding';
import { DataUriPhoto } from '@/modules/attendance/domain/valueObjects/DataUriPhoto';
import { FACE_PHOTO_JPEG_QUALITY } from '@/shared/config/terminal.config';
import { getQualityHint } from '@/shared/config/quality.config';
import type { QualityThresholds } from '@/shared/config/quality.config';
import { useQualitySettings } from '@/shared/hooks/useQualitySettings';
import type { QualityReport } from '@/modules/attendance/infrastructure/face/vendor/types';
import type { Employee } from '@/modules/employees/domain/entities/Employee';

// ── Capture steps (pixel-perfect from Clock_Web.html AdminEnrollFace) ─────

const captureSteps = [
  {
    key: 'frontal',
    label: 'Mira al frente',
    icon: (
      <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <circle cx="12" cy="12" r="9" /><circle cx="12" cy="11" r="2.5" />
        <path d="M8 17c1-1.6 2.3-2.4 4-2.4s3 .8 4 2.4" />
      </svg>
    ),
  },
  {
    key: 'derecha',
    label: 'Gira a la derecha',
    icon: (
      <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <polyline points="9 18 15 12 9 6" />
      </svg>
    ),
  },
  {
    key: 'izquierda',
    label: 'Gira a la izquierda',
    icon: (
      <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <polyline points="15 18 9 12 15 6" />
      </svg>
    ),
  },
  {
    key: 'arriba',
    label: 'Sube el mentón',
    icon: (
      <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <polyline points="18 15 12 9 6 15" />
      </svg>
    ),
  },
  {
    key: 'abajo',
    label: 'Baja el mentón',
    icon: (
      <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    ),
  },
] as const;

const tips = [
  {
    label: 'Iluminación uniforme',
    icon: (
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <circle cx="12" cy="12" r="4" />
        <line x1="12" y1="2" x2="12" y2="5" /><line x1="12" y1="19" x2="12" y2="22" />
        <line x1="2" y1="12" x2="5" y2="12" /><line x1="19" y1="12" x2="22" y2="12" />
      </svg>
    ),
  },
  {
    label: 'Rostro a 40 cm',
    icon: (
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <circle cx="12" cy="12" r="9" /><circle cx="12" cy="11" r="2.5" />
      </svg>
    ),
  },
  {
    label: 'Sin gafas ni gorra',
    icon: (
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
      </svg>
    ),
  },
  {
    label: 'Expresión neutra',
    icon: (
      <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <circle cx="12" cy="12" r="10" />
        <line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" />
        <line x1="8" y1="15" x2="16" y2="15" />
      </svg>
    ),
  },
];

import { poseValidators } from './poseValidators';

// ── Validate a quality report for the current step ────────────────────────
// General quality (blur/brightness/size) is handled upstream by getQualityHint.
// This function only delegates to the step-specific pose validator.
function validateForStep(q: QualityReport, stepIdx: number): { ok: boolean; feedback: string } {
  const validator = poseValidators[stepIdx];
  return validator ? validator(q.yawDeg, q.pitchDeg) : { ok: true, feedback: '' };
}

// ── Mean L2-normalized embedding ──────────────────────────────────────────
function meanL2Embedding(embeddings: number[][]): number[] {
  const dim = embeddings[0]?.length ?? 0;
  const sum = new Array<number>(dim).fill(0);
  for (const e of embeddings) {
    for (let i = 0; i < dim; i++) sum[i]! += (e[i] ?? 0);
  }
  let norm = 0;
  for (const v of sum) norm += v * v;
  norm = Math.sqrt(norm);
  if (norm < 1e-12) return sum;
  return sum.map((v) => v / norm);
}

// ── Frame capture helper ──────────────────────────────────────────────────
function captureFromVideo(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
): { frame: { data: Uint8ClampedArray; width: number; height: number; format: 'rgba' }; dataUri: string } | null {
  if (video.videoWidth === 0) return null;
  canvas.width  = video.videoWidth;
  canvas.height = video.videoHeight;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const dataUri   = canvas.toDataURL('image/jpeg', FACE_PHOTO_JPEG_QUALITY);
  return {
    frame:   { data: imageData.data, width: imageData.width, height: imageData.height, format: 'rgba' },
    dataUri,
  };
}

// ── Component ─────────────────────────────────────────────────────────────
type Phase = 'init' | 'loading-models' | 'camera-prompt' | 'running' | 'saving' | 'done' | 'error';

export function AdminEnrollFacePage() {
  const { C, T } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { idPerson } = useParams<{ idPerson: string }>();
  const { selectedRole } = useProfile();
  const { t } = useTranslation('common');
  const enrollFaceMutation = useEnrollFaceMutation();

  // Quality settings — load from db.settings; gate camera start until ready
  const {
    thresholds,
    matchThreshold,
    isLoaded: qualitySettingsLoaded,
    // Slice 3: Gate B override fields (consumer-side merge with vendor defaults per D1)
    blurMinLaplacianVar,
    brightnessMinGateB,
    brightnessMaxGateB,
    faceMinShortSidePx,
    // Slice 3: LivenessAggregator config from persisted settings
    passiveHigh,
    passiveLow,
    livenessWindowSize,
    livenessLiveCount,
    livenessSpoofConsecutive,
    livenessMaxTotalFrames,
  } = useQualitySettings();

  // Slice 3: build runtimeConfig from persisted Gate B settings; passed to both gates so they share thresholds
  const runtimeConfig: QualityRuntimeConfig = useMemo(() => ({
    blurMinLaplacianVar,
    brightnessMin: brightnessMinGateB,
    brightnessMax: brightnessMaxGateB,
    faceMinShortSidePx,
  }), [blurMinLaplacianVar, brightnessMinGateB, brightnessMaxGateB, faceMinShortSidePx]);

  // Slice 3: aggregator config from persisted settings (replaces DEFAULT_AGGREGATOR_CONFIG)
  const aggregatorConfig: AggregatorConfig = useMemo(() => ({
    passiveHigh,
    passiveLow,
    windowSize: livenessWindowSize,
    liveCount: livenessLiveCount,
    spoofConsecutive: livenessSpoofConsecutive,
    maxTotalFrames: livenessMaxTotalFrames,
  }), [passiveHigh, passiveLow, livenessWindowSize, livenessLiveCount, livenessSpoofConsecutive, livenessMaxTotalFrames]);

  // Refs for async/rAF callbacks — updated on each render so stale closures always read current values
  const enrollThresholdsRef = useRef<QualityThresholds>(thresholds);
  const matchThresholdRef   = useRef<number>(matchThreshold);
  const runtimeConfigRef    = useRef<QualityRuntimeConfig>(runtimeConfig);
  useEffect(() => { enrollThresholdsRef.current = thresholds; }, [thresholds]);
  useEffect(() => { matchThresholdRef.current   = matchThreshold; }, [matchThreshold]);
  useEffect(() => { runtimeConfigRef.current    = runtimeConfig; }, [runtimeConfig]);

  const employee = (location.state as { employee?: Employee } | null)?.employee ?? null;

  // stepIdx: 0..4 = which pose is being captured; 5 = all done
  const [phase, setPhase]           = useState<Phase>('init');
  const [stepIdx, setStepIdx]       = useState(0);
  const [qualityMsg, setQualityMsg] = useState('');
  const [errorMsg, setErrorMsg]     = useState('');
  const [cameraActive, setCameraActive] = useState(false);

  const videoRef   = useRef<HTMLVideoElement | null>(null);
  const canvasRef  = useRef<HTMLCanvasElement | null>(null);
  const streamRef  = useRef<MediaStream | null>(null);
  const rafRef     = useRef<number>(0);
  const lastFrameRef       = useRef<number>(0);
  const lastInferenceMsRef = useRef<number>(0); // tracks last analyzeQuality duration for adaptive pacing
  const analyzingRef    = useRef(false);   // mutex: prevents concurrent analyses
  const runningRef      = useRef(false);
  const stepIdxRef      = useRef(0);       // mirror of stepIdx for use inside async callbacks
  const capturedEmbeddings = useRef<number[][]>([]);
  const capturedPhotos     = useRef<string[]>([]);
  /**
   * Detection scale hint computed from DevicePerfProfile after model pre-warm.
   * Updated once during startEnrollment, after OnnxFaceMatcher.initialize() completes
   * and the pre-warm SCRFD latency is recorded.
   * Passed to every analyzeQuality call — inferEmbedding always receives the
   * original full-resolution frame regardless of this scale.
   */
  const scaleHintRef = useRef<number>(1.0);
  /**
   * Liveness aggregator — buffers liveness scores across frames to decide
   * live/spoof/inconclusive verdict. Reset on each step transition.
   * Slice 3: config sourced from persisted settings; re-initialized when settings change.
   */
  const aggregatorRef = useRef<LivenessAggregator>(
    new LivenessAggregator(DEFAULT_AGGREGATOR_CONFIG)
  );
  useEffect(() => {
    aggregatorRef.current = new LivenessAggregator(aggregatorConfig);
  }, [aggregatorConfig]);

  const isSuccess   = stepIdx >= captureSteps.length;
  const currentStep = !isSuccess ? captureSteps[stepIdx] : null;
  const progressPct = isSuccess ? 100 : (stepIdx / captureSteps.length) * 100;

  // ── Cleanup ───────────────────────────────────────────────────────────
  const stopEverything = useCallback(() => {
    runningRef.current = false;
    analyzingRef.current = false;
    cancelAnimationFrame(rafRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraActive(false);
  }, []);

  useEffect(() => () => { stopEverything(); }, [stopEverything]);

  // ── Save all 5 embeddings as mean ─────────────────────────────────────
  const saveEnrollment = useCallback(async () => {
    setPhase('saving');
    try {
      if (!idPerson || !selectedRole || !capturedEmbeddings.current.length) return;

      const meanVec   = capturedEmbeddings.current.length === 1
        ? capturedEmbeddings.current[0]!
        : meanL2Embedding(capturedEmbeddings.current);
      const embedding = FaceEmbedding.from(meanVec);

      // ── Duplicate-face check (configurable matchThreshold from quality settings) ─
      const duplicateCheck = await checkEnrollDuplicateFace(
        embedding,
        selectedRole.idAccessRole,
        Number(idPerson),
        matchThresholdRef.current,
      );
      if (duplicateCheck.isDuplicate) {
        navigate('/face-error', {
          replace: true,
          state: {
            action:    'enroll',
            idPerson,
            idEntity:  employee?.idEntity,
            errorCode: 'DUPLICATE_FACE',
            message:   'Another employee already has this face registered',
          },
        });
        return;
      }
      // ─────────────────────────────────────────────────────────────────────

      // ── Legacy duplicate-face pre-check (stricter threshold 0.75) ────────
      const gate = await runEnrollSaveGate({
        embedding,
        accessRoleId: selectedRole.idAccessRole,
        idPerson: Number(idPerson),
      });
      if (gate.conflict && gate.navigateTo) {
        navigate(gate.navigateTo, { replace: true, state: gate.navigateState });
        return;
      }
      // ─────────────────────────────────────────────────────────────────────

      const photo = DataUriPhoto.from(
        capturedPhotos.current[0] ?? 'data:image/jpeg;base64,/9j/4A==',
      );

      await enrollFaceMutation.mutateAsync({
        idPerson:     Number(idPerson),
        idEntity:     employee?.idEntity ?? 0,
        idAccessRole: selectedRole.idAccessRole,
        embedding,
        photoDataUri: photo,
        source:       'admin-manual',
      });

      setPhase('done');
      setTimeout(() => { navigate(-1); }, 2000);
    } catch (err) {
      setErrorMsg((err as Error).message ?? 'Error al guardar');
      setPhase('error');
    }
  }, [idPerson, selectedRole, employee, navigate, enrollFaceMutation]);

  // ── Per-frame analysis loop ───────────────────────────────────────────
  const loop = useCallback(() => {
    if (!runningRef.current) return;

    const now = performance.now();

    // Adaptive throttle: interval widens when inference is slow (nextInterval) to avoid overlap.
    // shouldSkip() was removed — it produced an infinite-skip lock on slow devices
    // (mobile) by deferring every rAF tick once lastInferenceMs exceeded 200ms.
    // nextInterval() alone provides correct throttling on slow devices.
    if (!analyzingRef.current && now - lastFrameRef.current >= nextInterval(lastInferenceMsRef.current)) {
      lastFrameRef.current = now;
      analyzingRef.current = true;

      const video  = videoRef.current;
      if (!canvasRef.current) canvasRef.current = document.createElement('canvas');
      const captured = video ? captureFromVideo(video, canvasRef.current) : null;

      if (captured) {
        const { frame, dataUri } = captured;
        const matcher = OnnxFaceMatcher.getInstance();
        const currentStepIdx = stepIdxRef.current;

        matcher.analyzeQuality(frame, scaleHintRef.current, runtimeConfigRef.current).then(({ ok: _ok, quality, hasOneFace, detection, reasons, durationMs }) => {
          // Update adaptive pacing: track inference duration for next interval calculation (AQG-2/3)
          lastInferenceMsRef.current = durationMs ?? 0;
          if (!runningRef.current) { analyzingRef.current = false; return; }

          if (!hasOneFace || !detection) {
            setQualityMsg(reasons[0]?.includes('2') || reasons[0]?.includes('multiple')
              ? 'Asegúrate de estar solo frente a la cámara'
              : 'Coloca tu rostro en el recuadro');
            analyzingRef.current = false;
            return;
          }

          // Use configurable thresholds from quality settings (enrollThresholdsRef keeps them fresh).
          // This enables runtime threshold adjustment via the admin config modal.
          const qualityHintKey = quality
            ? getQualityHint(
                {
                  blur:         quality.blurVar,
                  brightness:   quality.brightnessMean,
                  faceSize:     quality.faceShortSidePx,
                  faceDetected: true,
                },
                enrollThresholdsRef.current,
              )
            : 'scan.hint.noFace';

          if (qualityHintKey !== null) {
            const hintMsg =
              qualityHintKey === 'scan.hint.blur'        ? 'Quédate quieto, imagen borrosa' :
              qualityHintKey === 'scan.hint.darkScene'   ? 'Muy oscuro, mejora la iluminación' :
              qualityHintKey === 'scan.hint.brightScene' ? 'Muy brillante, evita la luz directa' :
              qualityHintKey === 'scan.hint.moveCloser'  ? 'Acércate a la cámara' :
              'Ajusta la posición del rostro';
            setQualityMsg(hintMsg);
            analyzingRef.current = false;
            return;
          }

          // General quality passed — now check step-specific pose direction
          const poseCheck = validateForStep(quality!, currentStepIdx);
          logTiming('[PoseValidator]', {
            stepIdx:  currentStepIdx,
            stepKey:  captureSteps[currentStepIdx]?.key ?? null,
            yaw:      Math.round(quality!.yawDeg),
            pitch:    Math.round(quality!.pitchDeg),
            ok:       poseCheck.ok,
            feedback: poseCheck.ok ? null : poseCheck.feedback,
          });
          if (!poseCheck.ok) {
            setQualityMsg(poseCheck.feedback);
            analyzingRef.current = false;
            return;
          }

          // ✅ All checks passed: infer embedding + liveness (MiniFASNetV2 runs inside inferEmbedding)
          setQualityMsg('');
          const inferStart = performance.now();
          withTimeout(
            matcher.inferEmbedding(frame, detection, runtimeConfigRef.current),
            8000,
            'inferEmbedding',
            new EmbeddingTimeoutError()
          )
            .then((result) => {
              const inferMs = performance.now() - inferStart;
              if (!runningRef.current) { analyzingRef.current = false; return; }

              // Handle discriminated result
              if (result.kind === 'low-quality') {
                // Quality regression between Gate A and Gate B — show subreason hint
                const hintMsg =
                  result.subreason === 'blur' ? 'Quédate quieto, imagen borrosa' :
                  result.subreason === 'brightness' ?
                    (result.report.brightnessMean < (enrollThresholdsRef.current.brightnessMin ?? 40)
                      ? 'Muy oscuro, mejora la iluminación'
                      : 'Muy brillante, evita la luz directa') :
                  result.subreason === 'size' ? 'Acércate a la cámara' :
                  'Ajusta la posición del rostro';
                setQualityMsg(hintMsg);
                logTiming('[InferEmbedding]', {
                  flow:       'enroll',
                  stepIdx:    currentStepIdx,
                  durationMs: inferMs,
                  ok:         false,
                  subreason:  result.subreason,
                });
                analyzingRef.current = false;
                return; // Continue loop — do NOT abort
              }

              // Embedding extracted — aggregate liveness score
              // Type guard: only 'embedding' variant reaches here (low-quality early-returns above)
              if (result.kind !== 'embedding') {
                // Should never happen, but handle defensively
                analyzingRef.current = false;
                return;
              }

              const { value: embedding, livenessScore } = result;
              const decision = aggregatorRef.current.push(livenessScore);

              logTiming('[InferEmbedding]', {
                flow:             'enroll',
                stepIdx:          currentStepIdx,
                durationMs:       inferMs,
                ok:               true,
                livenessScore:    livenessScore.toFixed(3),
                aggregatorVerdict: decision.verdict,
                ...(decision.verdict === 'pending' && { bufferSize: decision.bufferSize }),
              });

              if (decision.verdict === 'pending') {
                // Need more frames — continue loop without capturing
                analyzingRef.current = false;
                return;
              }

              if (decision.verdict === 'spoof') {
                // Spoof detected — navigate to error page
                runningRef.current = false;
                cancelAnimationFrame(rafRef.current);
                analyzingRef.current = false;
                navigate('/face-error', {
                  replace: true,
                  state: {
                    action:    'enroll',
                    idPerson,
                    idEntity:  (location.state as { idEntity?: number } | null)?.idEntity,
                    errorCode: 'LIVENESS_SPOOF',
                    consecutiveCount: decision.consecutiveCount,
                  },
                });
                return;
              }

              if (decision.verdict === 'inconclusive') {
                // Liveness inconclusive — navigate to error page
                runningRef.current = false;
                cancelAnimationFrame(rafRef.current);
                analyzingRef.current = false;
                navigate('/face-error', {
                  replace: true,
                  state: {
                    action:    'enroll',
                    idPerson,
                    idEntity:  (location.state as { idEntity?: number } | null)?.idEntity,
                    errorCode: 'LIVENESS_INCONCLUSIVE',
                    totalFrames: decision.totalFrames,
                  },
                });
                return;
              }

              // Verdict is 'live' — capture embedding and advance step
              capturedEmbeddings.current.push(embedding);
              capturedPhotos.current.push(dataUri);

              const fromStep = currentStepIdx;
              const nextStep = capturedEmbeddings.current.length;
              stepIdxRef.current = nextStep;
              setStepIdx(nextStep);

              // Reset aggregator for next step
              aggregatorRef.current.reset();

              logTiming('[StepTransition]', {
                from:       fromStep,
                to:         nextStep,
                stepKey:    captureSteps[fromStep]?.key ?? null,
                totalSteps: captureSteps.length,
                completed:  nextStep >= captureSteps.length,
              });

              if (nextStep >= captureSteps.length) {
                // All 5 steps done
                runningRef.current = false;
                cancelAnimationFrame(rafRef.current);
                analyzingRef.current = false;
                void saveEnrollment();
              } else {
                analyzingRef.current = false;
              }
            }).catch((err: unknown) => {
              // Every embedding error stops enrollment and redirects with an explicit errorCode.
              // Silent retry is prohibited (EEV-1..EEV-6). Timeout fires EmbeddingTimeoutError.
              const inferMs = performance.now() - inferStart;
              const errorCode = mapErrorToCode(err);
              logTiming('[InferEmbedding]', {
                flow:       'enroll',
                stepIdx:    currentStepIdx,
                durationMs: inferMs,
                ok:         false,
                errorCode,
              });
              runningRef.current = false;
              cancelAnimationFrame(rafRef.current);
              analyzingRef.current = false;
              void navigate('/face-error', {
                state: {
                  action:    'enroll',
                  idPerson,
                  idEntity:  (location.state as { idEntity?: number } | null)?.idEntity,
                  errorCode,
                },
                replace: true,
              });
            });
        }).catch(() => {
          analyzingRef.current = false;
        });
      } else {
        analyzingRef.current = false;
      }
    }

    rafRef.current = requestAnimationFrame(loop);
  }, [saveEnrollment, navigate, idPerson, location]);

  // ── Start enrollment ──────────────────────────────────────────────────
  const startEnrollment = useCallback(async () => {
    capturedEmbeddings.current = [];
    capturedPhotos.current     = [];
    stepIdxRef.current         = 0;
    analyzingRef.current       = false;
    setStepIdx(0);
    setQualityMsg('');
    setErrorMsg('');
    setPhase('loading-models');

    try {
      await OnnxFaceMatcher.getInstance().initialize();
    } catch {
      setPhase('error');
      setErrorMsg(t('admin.enrollFaceModelsUnavailable'));
      return;
    }

    // Compute detection scale hint from the pre-warm latency recorded during initialize().
    // classifyTier(undefined) → 'medium' → scaleHint=0.75 if pre-warm hasn't measured yet.
    // inferEmbedding ALWAYS receives the original full-resolution frame (scaleHint is for
    // SCRFD detection only — embedding compatibility across device tiers is preserved).
    const matcher = OnnxFaceMatcher.getInstance();
    const deviceTier = classifyTier(matcher.getPrewarmMs());
    scaleHintRef.current = detectionScaleForTier(deviceTier);

    setPhase('camera-prompt');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) { stopEverything(); return; }
      video.srcObject = stream;
      await video.play();
      setCameraActive(true);
      setPhase('running');
      runningRef.current  = true;
      lastFrameRef.current = 0;
      rafRef.current = requestAnimationFrame(loop);
    } catch {
      setPhase('error');
      setErrorMsg(t('admin.enrollFaceCameraDenied'));
    }
  }, [t, stopEverything, loop]);

  // ── Render ────────────────────────────────────────────────────────────
  const showSpinner = phase === 'loading-models' || phase === 'camera-prompt' || phase === 'saving';
  const statusLine  =
    phase === 'loading-models' ? t('admin.enrollFaceLoadingModels') :
    phase === 'camera-prompt'  ? t('admin.enrollFaceCameraPrompt')  :
    phase === 'saving'         ? t('admin.enrollFaceSaving')         : '';

  return (
    <EnrollmentScreenLayout
      onBack={() => { stopEverything(); navigate(-1); }}
      title={`${t('admin.enrollFaceTitle')} · ${employee?.fullName ?? `#${idPerson ?? ''}`}`}
      badgeLabel={`● ${t('admin.enrollEnrolamiento')}`}
      badgeColor={C.blue}
      T={T}
    >

        {/* Tips bar */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 18, flexWrap: 'wrap', marginBottom: 22, maxWidth: 620 }}>
          {tips.map((tip, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, color: T.textMuted, fontSize: 10, fontWeight: 600 }}>
              <div style={{ width: 22, height: 22, borderRadius: 6, background: `${C.blue}14`, color: C.blue, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {tip.icon}
              </div>
              {tip.label}
            </div>
          ))}
        </div>

        {/* Camera — size=210, identical to HTML prototype */}
        <div style={{ position: 'relative', marginBottom: 32 }}>
          <CameraFeed
            scanning={phase === 'running' && !isSuccess}
            size={210}
            T={T}
            videoRef={videoRef}
            cameraActive={cameraActive}
            showSpinner={phase === 'camera-prompt'}
          />
        </div>

        {/* Progress bar — 260×5px identical to HTML */}
        <div style={{ width: 260, height: 5, borderRadius: 3, background: T.border, overflow: 'hidden', marginBottom: 38 }}>
          <div style={{
            width: `${progressPct}%`,
            height: '100%',
            background: phase === 'done' || isSuccess ? C.success : C.blue,
            transition: 'width .4s ease',
          }} />
        </div>

        {/* State display */}
        {phase === 'done' || isSuccess ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 46, height: 46, borderRadius: '50%', background: C.success, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.success }}>{t('admin.enrollFaceDone')}</div>
          </div>

        ) : phase === 'error' ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 12, color: C.danger, fontWeight: 600, textAlign: 'center', maxWidth: 280 }}>{errorMsg}</div>
            <button
              onClick={() => { void startEnrollment(); }}
              style={{ padding: '8px 22px', borderRadius: 9, border: 'none', background: C.blue, color: '#fff', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
            >
              {t('admin.enrollFaceRetry')}
            </button>
          </div>

        ) : phase === 'init' && !qualitySettingsLoaded ? (
          /* Quality settings still loading from db — wait before allowing enrollment to start */
          <div data-testid="quality-settings-loading" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="mi-anim-spin" style={{ width: 14, height: 14, border: `2px solid ${T.textMuted}`, borderTopColor: C.blue, borderRadius: '50%' }} />
            <span style={{ fontSize: 11, fontWeight: 600, color: T.textMuted }}>{t('common.loading')}</span>
          </div>

        ) : phase === 'init' ? (
          <button
            onClick={() => { void startEnrollment(); }}
            style={{ padding: '10px 28px', borderRadius: 9, border: 'none', background: C.blue, color: '#fff', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, cursor: 'pointer', boxShadow: `0 4px 14px ${C.blue}40` }}
          >
            {t('admin.enrollFaceStart')}
          </button>

        ) : showSpinner ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="mi-anim-spin" style={{ width: 14, height: 14, border: `2px solid ${T.textMuted}`, borderTopColor: C.blue, borderRadius: '50%' }} />
            <span style={{ fontSize: 11, fontWeight: 600, color: T.text }}>{statusLine}</span>
          </div>

        ) : currentStep ? (
          /* Active step pill — shows quality feedback OR pose instruction */
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '9px 18px', borderRadius: 22,
            background: qualityMsg ? `${C.danger}12` : `${C.blue}12`,
            color: qualityMsg ? C.danger : C.blue,
          }}>
            {!qualityMsg && currentStep.icon}
            <div style={{ fontSize: 13, fontWeight: 700 }}>{qualityMsg || currentStep.label}</div>
          </div>

        ) : (
          <div style={{ fontSize: 12, color: T.textMuted, fontWeight: 600 }}>Preparando cámara…</div>
        )}

        {/* Step chips — identical to Clock_Web.html */}
        <div style={{ display: 'flex', gap: 8, marginTop: 44, flexWrap: 'wrap', justifyContent: 'center' }}>
          {captureSteps.map((s, i) => {
            const done   = i < capturedEmbeddings.current.length || phase === 'done';
            const active = stepIdx === i && phase === 'running';
            return (
              <div
                key={i}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '5px 10px', borderRadius: 14,
                  background: done   ? `${C.success}18` : active ? `${C.blue}18` : T.surfaceBg,
                  color:      done   ? C.success         : active ? C.blue        : T.textMuted,
                  fontSize: 9, fontWeight: 700,
                  border: `1px solid ${done ? `${C.success}33` : active ? `${C.blue}33` : T.border}`,
                }}
              >
                {done
                  ? <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
                  : (i + 1)
                }
                {' '}{s.key.charAt(0).toUpperCase() + s.key.slice(1)}
              </div>
            );
          })}
        </div>

      {/* Test-only trigger — never visible in production (display:none) */}
      <button
        data-testid="__test-trigger-save__"
        style={{ display: 'none' }}
        onClick={() => { void saveEnrollment(); }}
      />

    </EnrollmentScreenLayout>
  );
}
